import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/commonAPI';

interface Table {
    id: number;
    tableName: string;
    nfcUrl: string | null;
    isActive: boolean;
}

const getQrUrl = (url: string) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(url)}`;

const TableQrPrintPage: React.FC = () => {
    const navigate = useNavigate();
    const { storeId } = useParams<{ storeId: string }>();
    const [tables, setTables] = useState<Table[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!storeId) return;

        const fetchTables = async () => {
            setIsLoading(true);
            try {
                const response = await axiosInstance.get<Table[]>(`/api/stores/${storeId}/admin/tables`);
                setTables(response.data);
                setError(null);
            } catch {
                setError('테이블 QR 목록을 불러오지 못했습니다.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchTables();
    }, [storeId]);

    const printableTables = useMemo(() => {
        return tables.map(table => ({
            ...table,
            orderUrl: table.nfcUrl || `${window.location.origin}/order/store/${storeId}/table/${table.id}`,
        }));
    }, [tables, storeId]);

    return (
        <div style={pageStyle}>
            <style>{printStyle}</style>
            <header style={toolbarStyle} className="qr-print-toolbar">
                <button
                    type="button"
                    onClick={() => navigate(`/admin/stores/${storeId}/tables`)}
                    style={secondaryButtonStyle}
                >
                    뒤로가기
                </button>
                <div style={{ flex: 1 }}>
                    <h2 style={{ margin: 0 }}>테이블 QR 인쇄</h2>
                    <p style={toolbarDescriptionStyle}>인쇄해서 각 테이블에 붙이면 손님이 바로 주문할 수 있습니다.</p>
                </div>
                <button type="button" onClick={() => window.print()} style={primaryButtonStyle}>
                    인쇄하기
                </button>
            </header>

            {isLoading ? (
                <p style={messageStyle}>QR 목록을 불러오는 중...</p>
            ) : error ? (
                <p style={{ ...messageStyle, color: '#dc2626' }}>{error}</p>
            ) : printableTables.length === 0 ? (
                <p style={messageStyle}>등록된 테이블이 없습니다.</p>
            ) : (
                <main style={gridStyle}>
                    {printableTables.map(table => (
                        <section key={table.id} style={qrCardStyle}>
                            <div style={storeLabelStyle}>테이블 주문</div>
                            <h1 style={tableNameStyle}>{table.tableName}</h1>
                            <img
                                src={getQrUrl(table.orderUrl)}
                                alt={`${table.tableName} 주문 QR`}
                                style={qrImageStyle}
                            />
                            <p style={guideTextStyle}>카메라로 QR코드를 스캔해 주문해 주세요.</p>                            
                        </section>
                    ))}
                </main>
            )}
        </div>
    );
};

const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    padding: '24px',
    backgroundColor: '#f8fafc',
    boxSizing: 'border-box',
    fontFamily: 'sans-serif',
};

const toolbarStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '24px',
    padding: '16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#fff',
};

const toolbarDescriptionStyle: React.CSSProperties = {
    margin: '6px 0 0',
    color: '#64748b',
};

const primaryButtonStyle: React.CSSProperties = {
    minHeight: '42px',
    padding: '0 18px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#2563eb',
    color: '#fff',
    fontWeight: 800,
    cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
    ...primaryButtonStyle,
    backgroundColor: '#64748b',
};

const messageStyle: React.CSSProperties = {
    padding: '24px',
    color: '#475569',
    fontWeight: 700,
};

const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '18px',
};

const qrCardStyle: React.CSSProperties = {
    minHeight: '360px',
    padding: '22px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    backgroundColor: '#fff',
    textAlign: 'center',
    boxSizing: 'border-box',
    pageBreakInside: 'avoid',
};

const storeLabelStyle: React.CSSProperties = {
    color: '#2563eb',
    fontSize: '14px',
    fontWeight: 900,
};

const tableNameStyle: React.CSSProperties = {
    margin: '8px 0 16px',
    color: '#0f172a',
    fontSize: '28px',
};

const qrImageStyle: React.CSSProperties = {
    width: '220px',
    height: '220px',
};

const guideTextStyle: React.CSSProperties = {
    margin: '14px 0 8px',
    color: '#334155',
    fontWeight: 800,
};

const urlTextStyle: React.CSSProperties = {
    margin: 0,
    color: '#64748b',
    fontSize: '11px',
    lineBreak: 'anywhere',
};

const printStyle = `
@media print {
    @page {
        size: A4;
        margin: 12mm;
    }

    body {
        background: #fff !important;
    }

    .qr-print-toolbar {
        display: none !important;
    }
}
`;

export default TableQrPrintPage;
